import { Arrow } from "@radix-ui/react-tooltip";
import {
  BookCheck,
  Clock,
  HelpCircle,
  ShieldCheck,
  SquareArrowOutUpRight,
} from "lucide-react";
import { mdItParser } from "utils/markdown";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleLabel,
  CollapsibleSubLabel,
  CollapsibleTrigger,
} from "components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "components/ui/tooltip";
import ImageViewer from "components/ImageViewer";
import placeholderPicture from "images/profile-dummy-photo.png";

import { getPlatformProfileUrl, getUserName } from "utils/helpers/users";
import {
  formatIntegrityRisk,
  getIntegrityRisk,
  getIntegrityRiskExplanation,
  getIntegrityRiskStyles,
} from "utils/helpers/assessments";

const _formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
    return `${datePart} at ${timePart}`;
  } catch (e) {
    return dateString;
  }
};

const _formatDuration = (durationMinutes) => {
  const totalMinutes = Math.floor(durationMinutes || 0);
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const CandidateCard = ({ hit, assessment }) => {
  if (!assessment || !hit) {
    return null;
  }

  // The raw provider score, shown unqualified - the scale varies by provider
  // (Ropes reports out of 1000), so it is not a percentage.
  const finalScore =
    assessment.finalScore ?? assessment.resultDetails?.score ?? "-";
  const displayDuration = _formatDuration(assessment.timeTaken);
  const integrityRisk = getIntegrityRisk(assessment);
  const riskExplanation = getIntegrityRiskExplanation(assessment);

  const pdfResultUrl = assessment.pdfResultLocation
    ? `https://res.cloudinary.com/${
        process.env.REACT_APP_CLOUDINARY_CLOUDNAME
      }/image/upload/v${Date.now()}/${assessment.pdfResultLocation}.pdf`
    : "";

  const hitUsername = getUserName(hit);

  return (
    <div className="flex flex-col bg-white border border-slate-200 rounded-xl shadow-sm w-full overflow-hidden sticky top-0">
      <div className="pt-6 pb-4 flex flex-col items-center border-b border-slate-50">
        <div className="flex items-center justify-center overflow-hidden rounded-xl h-24 w-24 bg-slate-100 border-2 border-white shadow-sm mb-3">
          <ImageViewer
            objectKey={hit.headshotKey}
            responsive={false}
            placeholder={
              <img
                src={placeholderPicture}
                alt="placeholder"
                className="w-full h-full object-cover opacity-40"
              />
            }
          />
        </div>
        <div className="text-center px-2">
          <h3 className="text-sm font-semi text-slate-900 leading-tight">
            {hit.given_name} {hit.family_name}
          </h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
            {hitUsername ? (
              <a
                className="flex items-baseline gap-1 transition-all text-sky-500 font-bold opacity-70 hover:opacity-100 hover:underline w-fit"
                href={getPlatformProfileUrl(hitUsername)}
                target="_blank"
                rel="noreferrer"
              >
                @{hitUsername}
                <SquareArrowOutUpRight size={14} strokeWidth={2.5} />
              </a>
            ) : (
              "-"
            )}
          </p>
        </div>
      </div>

      <div className="p-3 space-y-3 bg-slate-50/30 border-b border-slate-50">
        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Clock size={14} />
            <span className="font-medium text-slate-500">Duration</span>
          </div>
          <span className="font-bold text-slate-700">{displayDuration}</span>
        </div>

        <div className="flex items-center justify-between text-[11px] group transition-colors">
          <div className="flex items-center gap-1.5 text-slate-400">
            <ShieldCheck
              size={13}
              className={getIntegrityRiskStyles(integrityRisk)}
            />
            <span className="font-medium text-slate-500">Integrity</span>
          </div>

          <div className="flex items-center gap-1.5">
            <span
              className={`px-1.5 py-0.5 rounded border font-bold uppercase text-[9px] transition-all ${getIntegrityRiskStyles(
                integrityRisk
              )}`}
            >
              {formatIntegrityRisk(integrityRisk) || "-"}
            </span>
            {riskExplanation && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle
                      size={11}
                      className="text-slate-500 group-hover:text-blue-500 transition-colors cursor-help"
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    className="max-w-[240px] bg-slate-900 text-white p-3 text-[11px] leading-relaxed"
                    sideOffset={10}
                    side="right"
                    align="center"
                  >
                    <div className="font-bold mb-1.5 flex items-center gap-1.5 border-b border-white/10 pb-1.5">
                      <ShieldCheck size={14} className="text-emerald-400" />
                      Behavioral Integrity Summary
                    </div>
                    <p className="opacity-90">{riskExplanation}</p>
                    <Arrow className="fill-slate-900" />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-1.5 text-slate-400">
            <BookCheck size={14} />
            <span className="font-medium text-slate-500">PDF Result</span>
          </div>
          <span className="font-bold text-slate-700">
            {pdfResultUrl ? (
              <a
                href={pdfResultUrl}
                target="_blank"
                rel="noreferrer"
                title="PDF Result"
                className="flex hover:text-sky-500"
              >
                <SquareArrowOutUpRight size={14} />
              </a>
            ) : (
              <p className="w-[24px] h-[24px] text-center">-</p>
            )}
          </span>
        </div>
      </div>

      <div className="mt-auto p-3 bg-blue-600 text-white text-center">
        <p className="text-[9px] font-bold uppercase tracking-[0.15em] opacity-80 mb-0.5">
          Overall Score
        </p>
        <div className="flex items-baseline justify-center">
          <span className="text-2xl font-black">{finalScore}</span>
        </div>
      </div>
    </div>
  );
};

const ResultDetails = ({ assessment }) => {
  if (!assessment) {
    return null;
  }

  const { resultDetails, assessmentCompleted } = assessment;

  const analysis = [...(resultDetails?.analysis || [])];

  return (
    <div>
      <header className="border-b border-slate-100 pb-6">
        <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
          Assessment Report
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Detailed breakdown of the evaluation{" "}
          {assessmentCompleted && (
            <>
              performed on{" "}
              <span className="text-xs">
                {_formatDate(assessmentCompleted)}
              </span>
            </>
          )}
        </p>
      </header>
      {analysis.length > 0 ? (
        <div className="space-y-4">
          {analysis.map((a, index) => {
            return (
              <Collapsible
                className="w-full rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                key={`analysis_${index}`}
              >
                <CollapsibleTrigger className="flex flex-col justify-start min-h-[60px] shadow pt-2">
                  <div className="w-full flex items-center justify-between p-3">
                    <div>
                      <CollapsibleLabel>
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-base font-bold text-slate-600">
                            {a.name}
                          </span>
                          <div>
                            <span className="text-sm uppercase font-bold text-slate-400 tracking-tight">
                              Score
                            </span>{" "}
                            <span className="text-sm font-black text-blue-600">
                              {a.score}/4
                            </span>
                          </div>
                        </div>
                      </CollapsibleLabel>
                      <CollapsibleSubLabel>
                        {a.summary && (
                          <p className="text-xs text-slate-500 mt-1.5 leading-relaxed max-w-2xl">
                            {a.summary}
                          </p>
                        )}
                      </CollapsibleSubLabel>
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div>
                    {a.explanation ? (
                      <article
                        className="prose"
                        dangerouslySetInnerHTML={{
                          __html: mdItParser(
                            a.explanation.replace(/\\n/g, "\n")
                          ),
                        }}
                      />
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-slate-300 py-20 border-2 border-dashed border-slate-100 rounded-[2rem]">
                        <p className="text-lg font-medium">Document is empty</p>
                        <p className="text-sm tracking-wide uppercase font-bold opacity-50">
                          Switch to source to start writing
                        </p>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      ) : (
        <p className="text-red-300 text-lg">No Report available</p>
      )}
    </div>
  );
};

const AssessmentReport = ({ assessment, hit }) => {
  return (
    <div className="flex-grow overflow-y-auto">
      <div className="mx-auto flex flex-col lg:flex-row gap-6 p-4 md:p-10">
        <div className="w-full lg:w-64 flex-shrink-0">
          <CandidateCard hit={hit} assessment={assessment} />
        </div>

        <div className="flex-grow w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-10 mb-4 md:mb-0 overflow-x-hidden">
          <ResultDetails assessment={assessment} />
        </div>
      </div>
    </div>
  );
};

export default AssessmentReport;
